import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { NetworkStatusService } from '../../_services/network-status/network-status.service';

@Component({
    selector: 'app-offline-banner',
        standalone: true,
imports: [TranslatePipe],
    templateUrl: './offline-banner.component.html',
    styleUrls: ['./offline-banner.component.scss']
})
export class OfflineBannerComponent implements OnInit, OnDestroy {
  private networkStatusService = inject(NetworkStatusService);
  private destroy$ = new Subject<void>();
  private onlineTimeout: any = null;
  private wasOffline = false;
  private hadQueuedRequests = false; // Track if we had requests to sync

  isOnline = true;
  queueCount = 0;
  isRetrying = false;
  isVisible = false;
  showBackOnline = false; // Show "back online" message

  ngOnInit(): void {
    // Subscribe to online status
    this.networkStatusService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online) => {
        // Check if we're coming back online from offline state
        if (online && this.wasOffline) {
          // Only show "back online" immediately if no queued requests
          if (this.queueCount === 0) {
            this.showBackOnlineBanner();
          } else {
            // Mark that we had queued requests - will show "back online" after sync
            this.hadQueuedRequests = true;
          }
        }
        this.wasOffline = !online;
        this.isOnline = online;
        this.updateVisibility();
      });

    // Subscribe to queue count
    this.networkStatusService.queuedRequests$
      .pipe(takeUntil(this.destroy$))
      .subscribe((queue) => {
        const previousCount = this.queueCount;
        this.queueCount = queue.length;

        // Show "back online" banner when queue becomes empty after syncing
        if (this.isOnline && this.hadQueuedRequests && previousCount > 0 && this.queueCount === 0) {
          this.hadQueuedRequests = false;
          this.showBackOnlineBanner();
        }

        this.updateVisibility();
      });

    // Subscribe to retry status
    this.networkStatusService.isRetrying$
      .pipe(takeUntil(this.destroy$))
      .subscribe((retrying) => {
        this.isRetrying = retrying;
      });
  }

  private updateVisibility(): void {
    // Show banner when offline OR when there are queued requests OR showing back online message
    this.isVisible = !this.isOnline || this.queueCount > 0 || this.showBackOnline;
  }

  private showBackOnlineBanner(): void {
    // Clear any existing timeout
    if (this.onlineTimeout) {
      clearTimeout(this.onlineTimeout);
    }

    this.showBackOnline = true;
    this.updateVisibility();
    // Don't auto-hide - user needs to click Refresh or Dismiss
  }

  dismissBanner(): void {
    this.showBackOnline = false;
    this.updateVisibility();
  }

  retryNow(): void {
    this.networkStatusService.retryAllRequests();
  }

  clearQueue(): void {
    this.networkStatusService.clearQueue();
  }

  refreshPage(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.onlineTimeout) {
      clearTimeout(this.onlineTimeout);
    }
  }
}
